(ns app.front.dashboard.map
  (:require ["@carbon/icons-react/es/close--filled/16" :default CloseFilled16]
            [app.domain.property.model :as property-model]
            [app.front.municipality :as municipality]
            [app.front.search-counts :as search-counts]
            [app.front.search-result :as sr]
            [app.front.view.map-common.util :as map-common-util]
            [app.front.view.workspace.map :as workspace-map]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(def ws-layer-id "workspaces")


(defn selected-point [db]
  (get-in db [::data ::selected-point]))


(defn point-dismiss-fx
  "Call with the db that still has the selected point."
  [db]
  (when-let [event (-> db ::search-mode :map-point-dismiss-event)]
    (when-let [{:keys [id]} (selected-point db)]
      [[:dispatch [event id]]])))


(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ search-mode]]
    (p/assoc-if {:db (assoc db
                            ::data {}
                            ::search-mode search-mode)}
                :fx (point-dismiss-fx db))))


(def data-search-keys [::map-results ::map-results-request-id])


(rf/reg-event-fx ::map-search
  (fn [{{{:keys [map-search-command name]} ::search-mode :as db} :db} [_ search-opts fit-fx]]
    (let [request-id (random-uuid)
          db         (-> db
                         (search-counts/start-request name)
                         (assoc-in [::data ::map-results-request-id] request-id))]
      {:db           db
       :comm/command {:command          map-search-command
                      :data             search-opts
                      :on-success       (search-counts/response-event db name [::map-search.success fit-fx request-id])
                      :on-error         (search-counts/error-event db name)
                      :finally-dispatch (search-counts/done-event db name nil)}})))


(rf/reg-event-fx ::map-search.success
  ;; Resetting ::data on init or a view switch also invalidates pending responses.
  (fn [{:keys [db]} [_ fit-fx request-id _ response]]
    (when (= request-id (get-in db [::data ::map-results-request-id]))
      ;; response contains :total-count & :geojson
      (merge {:db (assoc-in db [::data ::map-results] response)}
             (when (-> response :geojson :features seq)
               fit-fx)))))


(defn fit-map [^js ol-map tries]
  (p/if-all-let [view           (some-> ol-map .getView)
                 ws-layer       (->> (.getAllLayers ol-map)
                                     (filter #(= (.get ^js % "id") ws-layer-id))
                                     first)
                 cluster-source (.getSource ^js ws-layer)
                 source         (.getSource cluster-source)]
    (map-common-util/fit-map source view {:padding  [60 60 60 60]
                                          :max-zoom 15})
    ;; In case we were not ready, try again
    (when (< tries 5)
      (js/setTimeout #(fit-map ol-map (inc tries)) 100))))


(rf/reg-fx ::fit-map
  (fn [^js ol-map]
    (js/setTimeout #(fit-map ol-map 0) 100)))


(defn fetch-full-result?
  "Search modes whose map response does not carry everything the result view needs declare a
   `:search-command` for fetching the full result by id."
  [db]
  (-> db ::search-mode :search-command boolean))


(defn find-clj-feature [db ^js feature]
  (when-let [id (when feature (.getId feature))]
    (some-> db
            ::data
            ::map-results
            :geojson
            :features
            (p/find-first {:id id})
            :properties
            (assoc :id id)
            (cond-> (fetch-full-result? db) (assoc :loading? true)))))


(defn set-result-to-view [db {:keys [id] :as feature}]
  (-> (assoc-in db [::data ::result-in-view id] feature)
      (assoc-in [::data ::selected-result-id] id)))


(defn select-point [db point]
  (-> (update db ::data dissoc ::selected-result-id ::cluster-ids)
      (assoc-in [::data ::selected-point] point)))


(defn deselect-point [db]
  (p/dissoc-in db [::data ::selected-point]))


(defn point-clicked [db coordinates]
  (when-let [event (-> db ::search-mode :map-point-click-event)]
    {:fx [[:dispatch [event (vec coordinates)]]]}))


(defn single-feature-selected [db ^js feature]
  (when-let [clj-feature (find-clj-feature db feature)]
    (p/assoc-if {:db (-> (p/dissoc-in db [::data ::cluster-ids])
                         deselect-point
                         (set-result-to-view clj-feature))}
                :fx (point-dismiss-fx db)
                :dispatch (when (fetch-full-result? db)
                            [::result-fetch (:id clj-feature)]))))


(defn cluster-feature-selected [db features]
  (p/assoc-if {:db (->> features
                        (keep #(find-clj-feature db %))
                        (reduce (fn [db {:keys [id] :as clj-feature}]
                                  (-> (assoc-in db [::data ::result-in-view id] clj-feature)
                                      (p/dissoc-in [::data ::selected-result-id])
                                      (update-in [::data ::cluster-ids] (comp set conj) id)))
                                (-> (p/dissoc-in db [::data ::cluster-ids])
                                    deselect-point)))}
              :fx (point-dismiss-fx db)))


(rf/reg-event-fx ::map-click
  (map-common-util/click-handler-fn {:single-feature-fx        single-feature-selected
                                     :cluster-feature-fx       cluster-feature-selected
                                     :point-click-fx           point-clicked
                                     :search-success-evt       ::map-click.success
                                     :search-not-found-evt     ::map-click.not-found
                                     :cluster-layer-id         ws-layer-id
                                     :max-cluster-zoom         15
                                     :max-cluster-count-for-fx 5}))


(rf/reg-event-fx ::open-result-view
  (fn [{:keys [db]} [_ {:keys [id] :as feature}]]
    (p/assoc-if {:db (set-result-to-view db feature)}
                :dispatch (when (fetch-full-result? db)
                            [::result-fetch id]))))


(rf/reg-event-fx ::result-fetch
  (fn [{:keys [db]} [_ id]]
    (let [{:keys [search-command map-result-open-event]} (::search-mode db)]
      {:comm/command {:command          search-command
                      :data             {:id id}
                      :on-success       [::result-fetch.success id map-result-open-event]
                      :finally-dispatch [::result-fetch.done id]}})))


(defn result-fetch-success-fx [db id map-result-open-event {:keys [total-count results]}]
  (when (not= total-count 1)
    (js/console.error (str "Expected one result for id " id ", got " total-count)))
  ;; There should always be a result, but if we (due to a bug) do not get one,
  ;; we will not replace the result view with nil
  (if-let [res (first results)]
    (p/assoc-if {:db (assoc-in db [::data ::result-in-view id] res)}
                :dispatch (when (= id (get-in db [::data ::selected-result-id]))
                            (some-> map-result-open-event
                                    (vector id res))))
    {:db db}))

(rf/reg-event-fx ::result-fetch.success
  (fn [{:keys [db]} [_ id map-result-open-event _ response]]
    (result-fetch-success-fx db id map-result-open-event response)))


(rf/reg-event-db ::result-fetch.done
  (fn [db [_ id]]
    (p/dissoc-in db [::data ::result-in-view id :loading?])))


(defn result-dismiss-fx [db updated-db]
  (p/assoc-if {:db updated-db}
              :dispatch (when-let [id (get-in db [::data ::selected-result-id])]
                          (some-> db
                                  ::search-mode
                                  :map-result-dismiss-event
                                  (vector id)))))


(rf/reg-event-fx ::close-right-side-view
  (fn [{:keys [db]} _]
    (result-dismiss-fx db (update db ::data select-keys data-search-keys))))


(rf/reg-event-fx ::back-to-cluster-view
  (fn [{:keys [db]} _]
    (result-dismiss-fx db (p/dissoc-in db [::data ::selected-result-id]))))


(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::search-mode
  :-> ::search-mode)


(rf/reg-sub ::map-results
  :<- [::data]
  :-> ::map-results)


(rf/reg-sub ::total-count
  :<- [::map-results]
  :-> :total-count)


(rf/reg-sub ::selected-result-id
  :<- [::data]
  :-> ::selected-result-id)


(rf/reg-sub ::result-in-view
  :<- [::data]
  :<- [::selected-result-id]
  (fn [[data id] _]
    (get-in data [::result-in-view id])))


(rf/reg-sub ::cluster-ids
  :<- [::data]
  :-> ::cluster-ids)


(rf/reg-sub ::selected-point
  :<- [::data]
  :-> ::selected-point)


(rf/reg-sub ::cluster-results
  :<- [::data]
  :<- [::search-mode]
  :<- [::cluster-ids]
  (fn [[data {:keys [cluster-fn]} ids] _]
    (some->> ids
             (map #(get-in data [::result-in-view %]))
             seq
             cluster-fn
             (sort-by first))))


(rf/reg-sub ::map-geojson
  :<- [::map-results]
  :<- [::selected-result-id]
  :<- [::cluster-ids]
  (fn [[{:keys [geojson]} selected-id cluster-ids]]
    (if (or selected-id (seq cluster-ids))
      (update geojson
              :features
              (fn [features]
                (->> features
                     (map (fn [{:keys [id] :as feature}]
                            (if (or (= id selected-id)
                                    (and cluster-ids
                                         (contains? cluster-ids id)))
                              (assoc-in feature [:properties :selected] true)
                              feature))))))
      geojson)))


(defn right-side-view [& content]
  [:div
   {:class ["map-result-view" "relative" "rounded" "bg-ui-blue-10" "w-full" "mt-6"
            "lg:w-1-2" "xl:w-1-3" "lg:ml-2" "flex" "flex-col" "bg-ui-light-gray"]
    :style {:height "500px"}}
   (into [:<>] content)])


(defn close-button [tr]
  (cc/button {:icon       CloseFilled16
              :class-name "mr-2 mt-1"
              :kind       :ghost
              :size       :small
              :on-click   #(>evt [::close-right-side-view])}
             (tr [:close])))


(defn result-view [{:keys                          [tr result cluster-results]
                    {:keys [render-search-result]} :search-mode}]
  [right-side-view
   [:div
    {:class ["map-result-view__top" "map-right-side-box" "flex" "items-center"
             (if (seq cluster-results) "justify-between" "justify-end")
             (when (seq cluster-results) "pb-2")
             (sr/resolve-border-color result)]}
    (when (seq cluster-results)
      (cc/button {:icon          i/chevron-left
                  :icon-position :left
                  :class-name    "tight-icon-left-button mt-1 ml-1"
                  :kind          :ghost
                  :size          :small
                  :on-click      #(>evt [::back-to-cluster-view])}
                 (tr [:back])))
    [close-button tr]]

   [:div.flex-1.min-h-0.overflow-y-auto.rounded-b
    [render-search-result result {:map-search? true}]]])


(defn cluster-view [{:keys                              [tr cluster-results lang]
                     {:keys [render-clustered-results]} :search-mode}]
  [right-side-view
   [:div.flex-0
    {:class ["map-right-side-box" "flex" "items-center" "justify-end"]}
    [close-button tr]]
   [:div.p-4.flex-1.min-h-0.overflow-auto
    [render-clustered-results cluster-results #(>evt [::open-result-view %]) tr lang]]])


(defn map-and-details [{:keys [tr map-center search-opts domain-group-ids]}]
  (let [geojson-results (<sub [::map-geojson])
        search-mode     (<sub [::search-mode])
        result          (<sub [::result-in-view])
        cluster-results (<sub [::cluster-results])
        selected-point  (when (:render-map-point-view search-mode)
                          (<sub [::selected-point]))
        lang            (<sub [:language])
        total-count     (<sub [::total-count])
        muni-layers     (->> domain-group-ids
                             (mapcat #(<sub [::municipality/municipal-layers %]))
                             doall)
        layers          (concat (when geojson-results
                                  [{:type                    "geojson-cluster"
                                    :id                      ws-layer-id
                                    :title                   (tr [(:title search-mode)])
                                    :clusterMarkerSelectable true
                                    :featureCollection       geojson-results}])
                                muni-layers)]
    [:div.flex.items-center.flex-wrap.lg:flex-nowrap
     [:div.flex.map-boundary.map-extended.bg-gray-400.rounded.overflow-hidden.h-full.w-full.mt-6
      {:class (when (or result cluster-results selected-point)
                ["lg:w-1-2 xl:w-2-3" "lg:mr-2"])
       :style {:height "500px"}}
      [workspace-map/workspace-map
       (p/assoc-if
         {:center            map-center
          :layers            layers
          :extended?         true
          :preferGeolocation true
          :primaryMapMarkers (if-let [coordinates (:coordinates selected-point)]
                               [coordinates]
                               [])
          :fitMapButton      {:layer ws-layer-id
                              :text  (tr [:dashboard-search/view-all]
                                         [total-count])}
          :events            [{:eventType     "singleclick"
                               :eventFunction #(>evt [::map-click %])}
                              {:eventType     "moveend"
                               :eventFunction (fn [^js evt]
                                                (when-not geojson-results
                                                  (>evt [::map-search search-opts {::fit-map (.-map evt)}])))}]
          :zoom              8}
         :propertyGeometries (some->> selected-point
                                      :geometry
                                      property-model/geojson-feature
                                      (hash-map :primaryPropertyGeometry)))]]
     (cond
       result
       [result-view (util/mmap tr search-mode result cluster-results)]

       cluster-results
       [cluster-view (util/mmap cluster-results
                                search-mode
                                tr
                                lang)]

       selected-point
       [(:render-map-point-view search-mode) selected-point tr])]))
